import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, Pill } from "../components/ui";
import { ACTIVE_CHAIN } from "../config/chains";
import { activeLiveConfig } from "../services/launcher/useLauncher";
import {
  NETWORK_REGISTRY,
  STATUS_LABELS,
  type NetworkEntry,
} from "../config/networks";
import { degenAssets } from "../config/degenAssets";
import { MULTICHAIN_CARDS } from "../config/multichainCards";

/* ──────────────────────────────────────────────────────────────────────────
   Live stat strip — fetches real market count from API
────────────────────────────────────────────────────────────────────────── */
function StatStrip() {
  const [marketCount, setMarketCount] = useState<number | null>(null);

  useEffect(() => {
    fetch(`/api/fair-launch/markets?chainId=${ACTIVE_CHAIN.chainId}&limit=100`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        const count = Array.isArray(d?.markets)
          ? d.markets.length
          : typeof d?.total === "number"
          ? d.total
          : null;
        setMarketCount(count);
      })
      .catch(() => {});
  }, []);

  const stats = [
    { value: marketCount !== null ? String(marketCount) : "—", label: "MARKETS INDEXED", accent: "#27E7D4", live: true },
    { value: "0.0001 ETH", label: "LAUNCH FEE",    accent: "#B9FF45", live: false },
    { value: "46630",      label: "CHAIN ID",       accent: "#FF5CA8", live: false },
    { value: "TESTNET",    label: "NETWORK STATUS", accent: "#C084FC", live: false },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: "10px",
      }}
      className="heroStatsGrid"
    >
      {stats.map((s) => (
        <div
          key={s.label}
          style={{
            minHeight: "78px",
            padding: "14px 18px",
            background: "rgba(11, 10, 24, 0.96)",
            border: "1px solid rgba(255, 255, 255, 0.13)",
            borderRadius: "12px",
            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.035)",
          }}
        >
          <div className="flex items-center gap-1.5">
            {s.live && marketCount !== null && (
              <span className="w-1 h-1 rounded-full chain-dot-live shrink-0" />
            )}
            <span
              style={{
                fontSize: "clamp(20px, 1.8vw, 27px)",
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: s.accent,
                textShadow: "0 2px 16px rgba(0, 0, 0, 0.85)",
                fontFamily: "var(--font-mono, monospace)",
              }}
            >
              {s.value}
            </span>
          </div>
          <p
            style={{
              marginTop: "8px",
              color: "rgba(235, 235, 248, 0.72)",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontFamily: "var(--font-mono, monospace)",
            }}
          >
            {s.label}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Live terminal data row
────────────────────────────────────────────────────────────────────────── */
function TermRow({
  label, value, accent, href,
}: {
  label: string; value: string; accent?: boolean; href?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-plasma/8 last:border-0">
      <span className="text-muted/50 tracking-widest shrink-0 text-[11px] font-mono">{label}</span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-right leading-snug break-all text-[11px] font-mono hover:opacity-75 transition-opacity ${
            accent ? "text-plasma" : "text-muted/80"
          }`}
        >
          {value}
        </a>
      ) : (
        <span className={`text-right leading-snug break-all text-[11px] font-mono ${accent ? "text-plasma" : "text-muted/80"}`}>
          {value}
        </span>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Live market cards — max 3, fetched from real API
────────────────────────────────────────────────────────────────────────── */
interface MarketEntry {
  tokenAddress: string;
  marketAddress: string;
  name?: string;
  symbol?: string;
}

function LiveMarketCards() {
  const [markets, setMarkets] = useState<MarketEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/fair-launch/markets?chainId=${ACTIVE_CHAIN.chainId}&limit=3`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (Array.isArray(d?.markets)) setMarkets(d.markets.slice(0, 3));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded-xl h-28 animate-pulse"
            style={{ background: "rgba(119,17,255,0.06)", border: "1px solid rgba(119,17,255,0.1)" }}
          />
        ))}
      </div>
    );
  }

  if (markets.length === 0) {
    return <p className="text-center text-sm text-muted/40 font-mono py-6">No markets indexed yet</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {markets.map((m) => {
        const short = `${m.tokenAddress.slice(0, 6)}…${m.tokenAddress.slice(-4)}`;
        return (
          <Link
            to={`/fair-launch/${m.marketAddress}`}
            key={m.marketAddress}
            className="block rounded-xl p-5 group transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, rgba(119,17,255,0.08), rgba(5,6,10,0.9))",
              border: "1px solid rgba(119,17,255,0.2)",
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <p className="text-sm font-bold text-ink leading-tight">{m.name || "Unknown"}</p>
                <p className="text-xs font-mono text-plasma/70">{m.symbol || short}</p>
              </div>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-mono font-bold tracking-widest text-plasma/80 shrink-0"
                style={{ background: "rgba(0,229,199,0.08)", border: "1px solid rgba(0,229,199,0.2)" }}
              >
                <span className="w-1 h-1 rounded-full chain-dot-live" /> LIVE
              </span>
            </div>
            <p className="text-[10px] font-mono text-muted/40 break-all">{short}</p>
            <p className="text-xs font-mono text-muted/40 mt-2 group-hover:text-plasma/60 transition-colors">
              View market →
            </p>
          </Link>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Network chip (preserved)
────────────────────────────────────────────────────────────────────────── */
function NetworkChip({ network }: { network: NetworkEntry }) {
  const isLive = network.status === "live-testnet" || network.status === "live-mainnet";
  const isNext = network.status === "coming-next" || network.status === "next-major-network";
  const dotClass = isLive ? "chain-dot-live" : isNext ? "chain-dot-next" : "chain-dot-soon";

  const inner = (
    <div
      className="chain-card flex flex-col gap-1.5 rounded-lg border px-3 py-2.5"
      style={{
        borderColor: isLive ? "rgba(0,229,199,0.22)" : isNext ? "rgba(255,176,32,0.15)" : "rgba(28,34,51,0.6)",
        background: isLive ? "rgba(0,229,199,0.045)" : "rgba(11,14,22,0.5)",
      }}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs font-semibold text-ink leading-none truncate">{network.shortName}</span>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
      </div>
      <span className="text-[10px] text-muted/50 font-mono">{network.nativeAssetSymbol}</span>
      <span
        className={`text-[9px] font-mono font-bold tracking-widest leading-none ${
          isLive ? "text-plasma/70" : isNext ? "text-warn/60" : "text-muted/35"
        }`}
      >
        {STATUS_LABELS[network.status]}
      </span>
    </div>
  );

  const to = network.launchEnabled
    ? network.slug
      ? `/launch/${network.slug}`
      : "/launch"
    : "/networks";

  return <Link to={to}>{inner}</Link>;
}

/* ──────────────────────────────────────────────────────────────────────────
   Homepage
   Image order (per brief):
   1  neon_city_under_a_stormy_sky.png       → hero visual
   2  futuristic_robinhood_ui_panel.png      → Robinhood chain card
   3  base_interface_with_futuristic_neon_glow.png → Base card
   4  futuristic_solana_status_card_design.png    → Solana card
   5  mysterious_figure_in_neon_storm.png    → AI Studio mascot
   6  futuristic_rocket_with_neon_energy_bursts.png → launch workflow
   7  futuristic_cyberpunk_terminal_interface.png  → system terminal visual
   8  futuristic_crypto_token_dashboard_card.png  → market intelligence
   9  futuristic_crypto_trading_interface_design.png → trading experience
   10 futuristic_neon_ui_dashboard_display.png    → platform capabilities strip
────────────────────────────────────────────────────────────────────────── */

export default function Home() {
  const cfg = activeLiveConfig();
  const isLive = cfg.status === "live";
  const flAddr = ACTIVE_CHAIN.fairLaunchFactoryAddress;
  const stdAddr = ACTIVE_CHAIN.factoryAddress;

  return (
    <div className="space-y-0">

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 1 — HERO
          Full-width background: neon_city_under_a_stormy_sky.png (1672×941)
          object-fit: contain · text overlay on dark left area · no cropping
      ══════════════════════════════════════════════════════════════════ */}
      <section className="launchHero">
        {/* Background image — full 1672×941 composition, no cropping */}
        <img
          src={degenAssets.hero}
          alt=""
          className="launchHeroImage"
          draggable={false}
        />

        {/* Text overlaid on the existing dark left area of the image */}
        <div className="launchHeroContent">
          <div className="launchHeroCopy">

            <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: "22px" }}>
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-plasma/30 px-3 py-1 text-[11px] font-mono font-semibold text-plasma/90 tracking-widest uppercase"
                style={{ background: "rgba(0,229,199,0.07)" }}
              >
                <span className="w-1.5 h-1.5 rounded-full chain-dot-live animate-chain-pulse" />
                ROBINHOOD TESTNET · LIVE NOW
              </span>
              <span className="text-[11px] font-mono text-muted/40 tracking-wide">CHAIN 46630</span>
            </div>

            <h1
              className="font-hero text-ink"
              style={{ fontSize: "clamp(2.4rem, 4.8vw, 4.8rem)", lineHeight: 0.93, letterSpacing: "0.01em", marginBottom: "22px" }}
            >
              <span style={{ display: "block" }}>SYNAPZ FAIR LAUNCH.</span>
              <span style={{ display: "block" }}>CREATE. DISCOVER.</span>
              <span className="text-gradient-hero" style={{ display: "block" }}>TRADE MULTICHAIN.</span>
            </h1>

            <p className="text-muted" style={{ fontSize: "0.95rem", lineHeight: 1.65, maxWidth: "420px", marginBottom: "28px" }}>
              Create, discover and trade new tokens across multiple chains.
              Launching first on Robinhood Chain.
              No pre-mine. No private sale. No insider allocation.
            </p>

            <div className="flex flex-wrap gap-3" style={{ marginBottom: "16px" }}>
              <Link to="/launch">
                <button
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-150 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    padding: "13px 26px",
                    background: "linear-gradient(135deg, #7711ff, #e0006a)",
                    color: "#fff",
                    boxShadow: "0 0 0 1px rgba(119,17,255,0.5), 0 4px 28px rgba(119,17,255,0.45), 0 1px 0 rgba(255,255,255,0.1) inset",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M9.5 1L3 9.5h5.5L6 15l8-8.5H8.5L9.5 1z" />
                  </svg>
                  LAUNCH A TOKEN
                </button>
              </Link>
              <Link to="/markets">
                <button
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-bold tracking-wide text-ink transition-all duration-150 hover:text-plasma active:scale-[0.98]"
                  style={{
                    padding: "13px 26px",
                    border: "1px solid rgba(255,255,255,0.13)",
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  EXPLORE MARKETS
                </button>
              </Link>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <Link
                to="/launch-studio"
                className="heroStudioLink inline-flex items-center gap-1 transition-colors font-mono"
                style={{
                  display: "inline-flex",
                  color: "rgba(216, 180, 254, 0.92)",
                  fontSize: "13px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = "#FFFFFF";
                  (e.currentTarget as HTMLAnchorElement).style.textShadow = "0 0 16px rgba(192, 132, 252, 0.65)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = "rgba(216, 180, 254, 0.92)";
                  (e.currentTarget as HTMLAnchorElement).style.textShadow = "";
                }}
              >
                OPEN AI LAUNCH STUDIO →
              </Link>
            </div>

            {/* SYNAPZ Wallet — not yet production-active */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "20px",
                padding: "7px 14px",
                border: "1px solid rgba(119,17,255,0.25)",
                borderRadius: "8px",
                background: "rgba(119,17,255,0.06)",
              }}
            >
              <span style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.14em", fontFamily: "var(--font-mono, monospace)", color: "rgba(192,132,252,0.7)", textTransform: "uppercase" }}>
                SYNAPZ WALLET
              </span>
              <span style={{ fontSize: "10px", color: "rgba(235,235,248,0.35)", fontFamily: "var(--font-mono, monospace)", letterSpacing: "0.04em" }}>
                — COMING WITH THE LAUNCH ENGINE ROLLOUT
              </span>
            </div>

            <p
              style={{
                maxWidth: "520px",
                color: "rgba(218, 220, 235, 0.68)",
                fontSize: "12px",
                lineHeight: 1.65,
              }}
            >
              Independent tool for Robinhood Chain. Not affiliated with or endorsed by Robinhood.
              Creating a token does not guarantee liquidity, value, buyers or listing in the Robinhood app.
            </p>

          </div>
        </div>

      </section>

      {/* Stat strip — lives BELOW the hero in normal document flow */}
      <section className="heroStatsSection">
        <div className="heroStatsPanel">
          <StatStrip />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 2 — MULTICHAIN IDENTITY (7 cards: 3 PNG + 4 styled)
          Row 1: Robinhood · Base · Solana · Ethereum
          Row 2: XRP · Sui · Dogecoin (centred)
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-16"
        style={{ background: "rgba(5,3,10,0.97)", borderTop: "1px solid rgba(119,17,255,0.12)" }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 space-y-8">

          {/* Section header */}
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-line/50" />
              <p className="text-[10px] font-mono tracking-widest text-muted/40 uppercase whitespace-nowrap">
                Multichain Identity
              </p>
              <div className="h-px flex-1 bg-line/50" />
            </div>
            <p className="text-center text-[11px] font-mono text-muted/35">
              Launching first on Robinhood Chain. Solana is next. Base, Ethereum, SYNAPZ Chain and more on the roadmap.
            </p>
          </div>

          {/* 7 image cards — full artwork, no cropping */}
          <div className="multichain-grid">
            {MULTICHAIN_CARDS.map(card => (
              <Link key={card.key} to={card.link} className="block group">
                <div
                  className="rounded-xl overflow-hidden transition-transform duration-200 group-hover:-translate-y-1"
                  style={{
                    background: "rgba(8,4,18,0.7)",
                    border: "1px solid rgba(119,17,255,0.18)",
                    boxShadow: "0 0 32px rgba(80,0,180,0.06)",
                  }}
                >
                  {/* Full artwork — object-fit: contain, no cropping */}
                  <div className="multichain-card-media">
                    <img
                      src={card.image}
                      alt={card.title}
                      loading="lazy"
                      className="multichain-card-image"
                    />
                  </div>
                  {/* Compact metadata strip below artwork */}
                  <div
                    className="px-3 py-2 flex items-center justify-between gap-2"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
                  >
                    <p className="text-[10px] font-mono text-muted/45 truncate">{card.meta}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 3 — AI LAUNCH STUDIO
          Image 5: degenAssets.mascot
          /assets/synapz-degen/mysterious_figure_in_neon_storm.png
          Large portrait beside HTML text. Do not redraw the figure.
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-16"
        style={{
          background: "linear-gradient(135deg, #08020f 0%, #0b050f 50%, #06030c 100%)",
          borderTop: "1px solid rgba(220,25,255,0.1)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">

            {/* Image 5: mysterious_figure_in_neon_storm.png */}
            <div className="lg:w-[45%] shrink-0">
              <img
                src={degenAssets.mascot}
                alt="SYNAPZ AI Launch Studio"
                loading="lazy"
                style={{
                  width: "100%",
                  maxWidth: "520px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                  objectFit: "contain",
                  opacity: 0.96,
                  borderRadius: "20px",
                }}
              />
            </div>

            {/* HTML text */}
            <div className="lg:w-[55%] space-y-6">
              <div className="space-y-1">
                <p className="text-[10px] font-mono tracking-widest text-magenta/70 uppercase">Intelligence Layer</p>
                <h2
                  className="font-hero text-ink"
                  style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 0.95, letterSpacing: "0.01em" }}
                >
                  AI LAUNCH<br />STUDIO
                </h2>
              </div>
              <p className="text-muted text-base leading-relaxed max-w-lg">
                The AI agent that reads the chain so you don't have to.
                Signals, sentiment, curve positioning — distilled into an edge
                that degens actually use.
              </p>
              <ul className="space-y-3">
                {[
                  { accent: "#ff2d9b", label: "Token signal scoring" },
                  { accent: "#9d00ff", label: "Launch timing intelligence" },
                  { accent: "#00e5c7", label: "Curve positioning advisor" },
                  { accent: "#a3ff2e", label: "On-chain risk scanner" },
                ].map(({ accent, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm text-muted/80">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                    {label}
                  </li>
                ))}
              </ul>
              <Link to="/launch-studio">
                <button
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-150 hover:brightness-110 hover:scale-[1.02]"
                  style={{
                    padding: "13px 26px",
                    background: "linear-gradient(135deg, rgba(220,25,255,0.9), rgba(157,0,255,0.9))",
                    color: "#fff",
                    boxShadow: "0 0 0 1px rgba(220,25,255,0.4), 0 4px 24px rgba(220,25,255,0.3)",
                  }}
                >
                  OPEN AI STUDIO →
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 4 — LAUNCH WORKFLOW
          Image 6: degenAssets.rocket
          /assets/synapz-degen/futuristic_rocket_with_neon_energy_bursts.png
          Alongside "Start a Launch" copy. Do not build another rocket.
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-16"
        style={{ background: "rgba(6,4,14,0.98)", borderTop: "1px solid rgba(119,17,255,0.1)" }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16">

            {/* Image 6: futuristic_rocket_with_neon_energy_bursts.png */}
            <div className="lg:w-[44%] shrink-0">
              <img
                src={degenAssets.rocket}
                alt="Launch a token on SYNAPZ"
                loading="lazy"
                style={{
                  width: "100%",
                  maxWidth: "480px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                  objectFit: "contain",
                  opacity: 0.96,
                  borderRadius: "16px",
                }}
              />
            </div>

            {/* HTML text */}
            <div className="lg:w-[56%] space-y-6">
              <div className="space-y-1">
                <p className="text-[10px] font-mono tracking-widest text-acid/70 uppercase">Launch Protocol</p>
                <h2
                  className="font-hero text-ink"
                  style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 0.95, letterSpacing: "0.01em" }}
                >
                  FAIR LAUNCH<br />PROTOCOL
                </h2>
              </div>
              <p className="text-muted text-base leading-relaxed max-w-lg">
                Set a name. Pick a chain. Launch in minutes.
                Every token starts from zero — no presale, no insider bag, no VC unlock.
                Just the curve, the community, and the chain.
              </p>
              <ul className="space-y-3">
                {[
                  { accent: "#a3ff2e", label: "1B token supply — 80% bonding curve, 20% DEX" },
                  { accent: "#00e5c7", label: "Creator buys through the curve like everyone else" },
                  { accent: "#7711ff", label: "Graduation to DEX at verified curve valuation" },
                  { accent: "#ff2d9b", label: "LP locked 30 days post-graduation" },
                ].map(({ accent, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm text-muted/80">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                    {label}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <Link to="/fair-launch/create">
                  <button
                    className="inline-flex items-center gap-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-150 hover:brightness-110 hover:scale-[1.02]"
                    style={{
                      padding: "13px 26px",
                      background: "linear-gradient(135deg, #7711ff, #e0006a)",
                      color: "#fff",
                      boxShadow: "0 0 0 1px rgba(119,17,255,0.5), 0 4px 24px rgba(119,17,255,0.4)",
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="M9.5 1L3 9.5h5.5L6 15l8-8.5H8.5L9.5 1z" />
                    </svg>
                    CREATE FAIR LAUNCH
                  </button>
                </Link>
                <Link to="/fair-launch">
                  <button
                    className="inline-flex items-center gap-2 rounded-lg text-sm font-bold tracking-wide text-ink transition-all hover:text-plasma"
                    style={{ padding: "13px 26px", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)" }}
                  >
                    Browse Markets →
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 5 — SYSTEM TERMINAL
          Image 7: degenAssets.terminal
          /assets/synapz-degen/futuristic_cyberpunk_terminal_interface.png
          Visual asset on one side. Live HTML chain data on the other.
          Real values: network, chain ID, factory addresses, launch fee, VM, status.
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-16"
        style={{
          background: "linear-gradient(135deg, #04060e, #030107)",
          borderTop: "1px solid rgba(0,229,199,0.08)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-12">

            {/* Image 7: futuristic_cyberpunk_terminal_interface.png */}
            <div className="lg:w-[48%] shrink-0">
              <img
                src={degenAssets.terminal}
                alt="SYNAPZ System Terminal"
                loading="lazy"
                style={{
                  width: "100%",
                  maxWidth: "540px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                  objectFit: "contain",
                  opacity: 0.95,
                  borderRadius: "16px",
                }}
              />
            </div>

            {/* Live HTML data panel — real values from chain config */}
            <div className="lg:w-[52%] space-y-5">
              <div className="space-y-1">
                <p className="text-[10px] font-mono tracking-widest text-plasma/60 uppercase">System Status</p>
                <h2
                  className="font-hero text-ink"
                  style={{ fontSize: "clamp(2rem, 4.5vw, 3.2rem)", lineHeight: 0.95, letterSpacing: "0.01em" }}
                >
                  NETWORK<br />TERMINAL
                </h2>
              </div>
              <div
                className="rounded-xl p-5 font-mono"
                style={{ background: "rgba(0,229,199,0.04)", border: "1px solid rgba(0,229,199,0.15)" }}
              >
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-plasma/10">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${isLive ? "chain-dot-live" : "chain-dot-next"}`} />
                  <span className={`text-xs font-bold tracking-widest uppercase ${isLive ? "text-plasma" : "text-warn/80"}`}>
                    {isLive ? "SYSTEM ONLINE — ACCEPTING LAUNCHES" : "PREVIEW MODE"}
                  </span>
                </div>
                <TermRow label="NETWORK"     value="Robinhood Chain Testnet" />
                <TermRow label="CHAIN ID"    value="46630" accent />
                <TermRow
                  label="STD FACTORY"
                  value={stdAddr ? `${stdAddr.slice(0, 10)}…${stdAddr.slice(-6)}` : "—"}
                  accent
                  href={stdAddr ? `${ACTIVE_CHAIN.explorerUrl}/address/${stdAddr}` : undefined}
                />
                <TermRow
                  label="FL FACTORY"
                  value={flAddr ? `${flAddr.slice(0, 10)}…${flAddr.slice(-6)}` : "—"}
                  accent
                  href={flAddr ? `${ACTIVE_CHAIN.explorerUrl}/address/${flAddr}` : undefined}
                />
                <TermRow label="LAUNCH FEE"  value="0.0001 ETH" />
                <TermRow label="VM"          value="EVM · Solidity 0.8.24" />
                <div className="flex items-center gap-1.5 text-plasma/50 mt-3 pt-3 border-t border-plasma/8">
                  <span className="text-xs">{">"}</span>
                  <span className="text-xs text-plasma/80">ready_</span>
                  <span
                    className="inline-block w-[7px] h-[12px] bg-plasma/70"
                    style={{ animation: "node-blink 1s step-end infinite" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 6 — MARKET INTELLIGENCE
          Image 8: degenAssets.market
          /assets/synapz-degen/futuristic_crypto_token_dashboard_card.png
          Illustrative visual — labeled as such. Real API market cards below.
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-16"
        style={{ background: "rgba(5,3,10,0.98)", borderTop: "1px solid rgba(119,17,255,0.1)" }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 space-y-8">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-line/50" />
            <p className="text-[10px] font-mono tracking-widest text-muted/40 uppercase whitespace-nowrap">
              Live Market Intelligence
            </p>
            <div className="h-px flex-1 bg-line/50" />
          </div>

          {/* Image 8: futuristic_crypto_token_dashboard_card.png — illustrative */}
          <div>
            <img
              src={degenAssets.market}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                maxWidth: "600px",
                height: "auto",
                display: "block",
                margin: "0 auto 8px",
                objectFit: "contain",
                opacity: 0.95,
                borderRadius: "16px",
              }}
            />
            <p className="text-center text-[10px] font-mono text-muted/30 tracking-widest">
              ILLUSTRATIVE — NOT CURRENT MARKET DATA
            </p>
          </div>

          {/* Real live market cards from API */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono text-muted/50 tracking-widest uppercase">
                Live Markets — Robinhood Testnet
              </p>
              <Link to="/fair-launch" className="text-xs font-mono text-plasma/60 hover:text-plasma transition-colors">
                View all →
              </Link>
            </div>
            <LiveMarketCards />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 7 — TRADING EXPERIENCE
          Image 9: degenAssets.trading
          /assets/synapz-degen/futuristic_crypto_trading_interface_design.png
          Illustrative visual alongside HTML copy.
          The real buy/sell trading form remains live HTML on the market page.
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-16"
        style={{
          background: "linear-gradient(135deg, #06030c, #08020f)",
          borderTop: "1px solid rgba(255,45,155,0.08)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-16">

            {/* Image 9: futuristic_crypto_trading_interface_design.png */}
            <div className="lg:w-[46%] shrink-0">
              <img
                src={degenAssets.trading}
                alt="SYNAPZ trading interface"
                loading="lazy"
                style={{
                  width: "100%",
                  maxWidth: "440px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                  objectFit: "contain",
                  opacity: 0.96,
                  borderRadius: "20px",
                }}
              />
            </div>

            {/* HTML text */}
            <div className="lg:w-[54%] space-y-6">
              <div className="space-y-1">
                <p className="text-[10px] font-mono tracking-widest text-magenta/70 uppercase">Trading Experience</p>
                <h2
                  className="font-hero text-ink"
                  style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 0.95, letterSpacing: "0.01em" }}
                >
                  TRADE WITH<br />PRECISION
                </h2>
              </div>
              <p className="text-muted text-base leading-relaxed max-w-lg">
                Every market comes with a live buy/sell panel.
                Real wallet balance. Live quote. Price impact. Simulation before signing.
                The full trade flow — on-chain, transparent, no intermediaries.
              </p>
              <ul className="space-y-3">
                {[
                  { accent: "#ff2d9b", label: "Live wallet balance and quote updates" },
                  { accent: "#9d00ff", label: "Price impact and minimum received" },
                  { accent: "#00e5c7", label: "Transaction simulation before signing" },
                  { accent: "#a3ff2e", label: "Bonding curve math, fully on-chain" },
                ].map(({ accent, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm text-muted/80">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                    {label}
                  </li>
                ))}
              </ul>
              <Link to="/fair-launch">
                <button
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-bold tracking-wide transition-all duration-150 hover:brightness-110 hover:scale-[1.02]"
                  style={{
                    padding: "13px 26px",
                    background: "linear-gradient(135deg, #ff2d9b, #9d00ff)",
                    color: "#fff",
                    boxShadow: "0 0 0 1px rgba(255,45,155,0.4), 0 4px 24px rgba(255,45,155,0.3)",
                  }}
                >
                  FIND A MARKET →
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 8 — PLATFORM CAPABILITIES
          Image 10: degenAssets.features
          /assets/synapz-degen/futuristic_neon_ui_dashboard_display.png
          Full-width image. The approved feature strip. Do not recreate cards.
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="py-12"
        style={{
          background: "rgba(4,5,12,0.99)",
          borderTop: "1px solid rgba(0,229,199,0.07)",
          borderBottom: "1px solid rgba(0,229,199,0.07)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-line/50" />
            <p className="text-[10px] font-mono tracking-widest text-muted/40 uppercase whitespace-nowrap">
              Platform Capabilities
            </p>
            <div className="h-px flex-1 bg-line/50" />
          </div>
          {/* Image 10: futuristic_neon_ui_dashboard_display.png — full width */}
          <img
            src={degenAssets.features}
            alt="SYNAPZ platform capabilities — fair launch, AI studio, live intelligence, wallet simulation, multichain, on-chain verified, testnet live"
            loading="lazy"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              objectFit: "contain",
              opacity: 0.96,
              borderRadius: "12px",
            }}
          />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          CCC MEMBERS PROMO
      ══════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16">
        <div
          className="relative overflow-hidden rounded-2xl px-7 py-8 sm:px-10 flex flex-col sm:flex-row items-center gap-6"
          style={{
            background: "linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(88,101,242,0.08) 50%, rgba(5,6,10,0.92) 100%)",
            border: "1px solid rgba(139,92,246,0.25)",
            boxShadow: "0 0 0 1px rgba(139,92,246,0.06), 0 12px 48px -12px rgba(139,92,246,0.15)",
          }}
        >
          <img
            src="/assets/ccc/ccc-members-synapz-launchpad.png"
            alt="CCC Members — SYNAPZ Launchpad"
            loading="lazy"
            className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 object-contain drop-shadow-[0_0_24px_rgba(139,92,246,0.35)]"
          />
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[10px] font-mono tracking-widest text-purple-400 uppercase mb-2">
              Partnership
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              Crypto Calls Crew <span className="text-purple-400">×</span> SYNAPZ
            </h2>
            <p className="mt-2 text-sm text-gray-300 max-w-2xl">
              Crypto Calls Crew members receive enhanced access to SYNAPZ tools, advanced
              intelligence, AI benefits, creator support and private testnet opportunities.
            </p>
          </div>
          <Link
            to="/partners/ccc"
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-semibold transition-colors"
          >
            Explore CCC Benefits
          </Link>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          FAIR LAUNCH HIGHLIGHT (preserved)
      ══════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div
          className="relative overflow-hidden rounded-2xl px-7 py-10 sm:px-10"
          style={{
            background: "linear-gradient(135deg, rgba(0,229,199,0.06) 0%, rgba(157,0,255,0.09) 50%, rgba(5,6,10,0.92) 100%)",
            border: "1px solid rgba(0,229,199,0.2)",
            boxShadow: "0 0 0 1px rgba(0,229,199,0.05), 0 12px 48px -12px rgba(0,229,199,0.12)",
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-20 -right-20 w-80 h-80 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(157,0,255,0.14) 0%, transparent 65%)" }}
          />
          <div aria-hidden="true" className="absolute inset-0 grid-etch-fine opacity-30 rounded-2xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
            <div className="space-y-4 max-w-xl">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="plasma">FAIR LAUNCH · BETA</Pill>
                <span className="text-[10px] font-mono text-muted/40">Bonding curve · Robinhood Testnet</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink">
                The fairest way to launch a token.
              </h2>
              <p className="text-muted text-sm sm:text-base leading-relaxed">
                Launch with a built-in bonding curve market. Price starts low, rises as buyers join,
                and graduates to a DEX at a verified curve-implied valuation.
                No pre-mine. No insider allocation. Everyone buys on the same curve.
              </p>
              <ul className="text-sm text-muted space-y-2">
                {[
                  "1B token supply — 80% on curve, 20% to DEX pool",
                  "Creator buys through the curve just like everyone else",
                  "Graduation determined by verified curve state, not gross volume",
                  "LP locked 30 days post-graduation",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-plasma mt-0.5 shrink-0 font-bold">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-3 shrink-0 sm:items-end">
              <Link to="/fair-launch">
                <Button className="px-7 py-2.5 w-full sm:w-auto">Explore Fair Launch →</Button>
              </Link>
              <Link to="/fair-launch/create">
                <Button variant="ghost" className="px-7 py-2.5 w-full sm:w-auto text-sm">
                  Create Fair Launch
                </Button>
              </Link>
              <p className="text-[11px] text-muted/40 sm:text-right font-mono">
                Contracts on testnet · economics finalising
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          NETWORK ROADMAP (preserved)
      ══════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-16 space-y-5">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <p className="text-[10px] font-mono text-muted/40 tracking-widest uppercase">Supported Networks</p>
            <h2 className="font-display text-xl font-bold text-ink">Network Roadmap</h2>
          </div>
          <Link to="/networks" className="text-xs text-plasma/70 hover:text-plasma transition-colors font-mono">
            View full roadmap →
          </Link>
        </div>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4">
          {NETWORK_REGISTRY.map((n) => (
            <NetworkChip key={n.key} network={n} />
          ))}
        </div>
      </section>

    </div>
  );
}
